import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { DCard, BtnGold, BtnGhost } from './MarketplaceAtoms.js';
import { AD_MEDIA_TYPE_OPTIONS, AD_SLOT_OPTIONS, getAdSlotSizeHint } from '../../lib/adSlotsConfig.js';
import {
  fetchAdminAdForEdit,
  saveAdminAdCampaign,
  uploadAdBannerMedia,
  validateAdMediaFile,
} from '../../lib/adminAdsService.js';

function toDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(s) {
  if (!s || !String(s).trim()) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

const fieldLabel = { fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4 };
const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '0.5px solid rgba(255,255,255,0.12)',
  background: 'rgba(0,0,0,0.35)',
  color: '#fff',
  fontSize: 13,
  boxSizing: 'border-box',
};

const STATUS_OPTIONS = [
  { value: 'borrador', label: 'Borrador' },
  { value: 'pendiente_revision', label: 'Pendiente revisión' },
  { value: 'aprobado', label: 'Aprobado' },
  { value: 'activo', label: 'Activo (visible en web)' },
];

/**
 * Crear o editar campaña publicitaria (Admin).
 */
export default function AdminAdCampaignForm({ adId, initialSlot = 'hero_marketplace', onClose, onSaved, onError }) {
  const isEdit = !!adId;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [slot, setSlot] = useState(initialSlot || 'hero_marketplace');
  const [sortOrder, setSortOrder] = useState(0);
  const [mediaType, setMediaType] = useState('image');
  const [targetUrl, setTargetUrl] = useState('https://dreamvinyl.es');
  const [altText, setAltText] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [status, setStatus] = useState('borrador');
  const [notes, setNotes] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  const slotHint = useMemo(() => getAdSlotSizeHint(slot), [slot]);

  const loadAd = useCallback(async () => {
    if (!adId) {
      setLoading(false);
      const start = new Date();
      const end = new Date();
      end.setDate(end.getDate() + 30);
      setStartsAt(toDatetimeLocal(start.toISOString()));
      setEndsAt(toDatetimeLocal(end.toISOString()));
      setSlot(initialSlot || 'hero_marketplace');
      setSortOrder(0);
      return;
    }
    setLoading(true);
    onError('');
    try {
      const row = await fetchAdminAdForEdit(adId);
      if (!row) throw new Error('Campaña no encontrada');
      setCampaignName(row.campaign_name || '');
      setSlot(row.slot || 'hero_marketplace');
      setMediaType(row.media_type || 'image');
      setTargetUrl(row.target_url || 'https://dreamvinyl.es');
      setAltText(row.alt_text || '');
      setStartsAt(toDatetimeLocal(row.starts_at));
      setEndsAt(toDatetimeLocal(row.ends_at));
      setStatus(row.status || 'borrador');
      setNotes(row.notes || '');
      setMediaUrl(row.media_url || '');
      setSortOrder(row.sort_order ?? 0);
    } catch (e) {
      onError(e?.message || 'No se pudo cargar la campaña');
    } finally {
      setLoading(false);
    }
  }, [adId, initialSlot, onError]);

  useEffect(() => {
    loadAd();
  }, [loadAd]);

  useEffect(() => {
    if (!file) {
      setFilePreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setFilePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const previewSrc = filePreview || mediaUrl || null;
  const previewIsVideo =
    file?.type?.startsWith('video/') || (!file && String(mediaType).toLowerCase() === 'video');

  const handleSubmit = async e => {
    e.preventDefault();
    onError('');
    const starts = fromDatetimeLocal(startsAt);
    const ends = fromDatetimeLocal(endsAt);
    if (!starts || !ends) {
      onError('Indica fechas de inicio y fin válidas');
      return;
    }
    let startsIso = starts;
    let endsIso = ends;
    if (status === 'activo') {
      const now = new Date();
      if (new Date(startsIso) > now) startsIso = now.toISOString();
      if (new Date(endsIso) <= now) {
        const end = new Date(now);
        end.setDate(end.getDate() + 30);
        endsIso = end.toISOString();
      }
    }
    const fileErr = file ? validateAdMediaFile(file, mediaType) : null;
    if (fileErr) {
      onError(fileErr);
      return;
    }
    if (!file && !mediaUrl) {
      onError('Sube una imagen o vídeo para la campaña');
      return;
    }

    setSaving(true);
    try {
      let finalMediaUrl = mediaUrl;
      let id = adId;

      if (!id) {
        id = await saveAdminAdCampaign({
          campaignName,
          slot,
          mediaType,
          mediaUrl: mediaUrl || 'https://placehold.co/1x1/000000/000000.png',
          targetUrl,
          altText,
          startsAt: startsIso,
          endsAt: endsIso,
          status,
          notes,
          sortOrder,
          campaignType: slot === 'hero_marketplace' ? 'hero' : 'display',
        });
      }

      if (file) {
        finalMediaUrl = await uploadAdBannerMedia(file, id, mediaType);
      }

      await saveAdminAdCampaign(
        {
          campaignName,
          slot,
          mediaType,
          mediaUrl: finalMediaUrl,
          targetUrl,
          altText,
          startsAt: startsIso,
          endsAt: endsIso,
          status,
          notes,
          sortOrder,
          campaignType: slot === 'hero_marketplace' ? 'hero' : 'display',
        },
        id
      );

      onSaved?.();
      onClose?.();
    } catch (err) {
      onError(err?.message || 'No se pudo guardar la campaña');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DCard style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Cargando formulario…</div>
      </DCard>
    );
  }

  return (
    <DCard style={{ padding: 18, marginBottom: 16, borderColor: 'rgba(200,168,75,0.25)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.gold }}>
            {isEdit ? 'Editar campaña' : 'Nueva campaña'}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 4, lineHeight: 1.45 }}>
            Sube imagen, GIF o vídeo. Anunciante interno DreamVinyl. Activa la campaña cuando las fechas sean correctas.
          </div>
        </div>
        <BtnGhost type="button" onClick={onClose} disabled={saving} style={{ width: 'auto', minWidth: 90 }}>
          Cerrar
        </BtnGhost>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
          <div>
            <div style={fieldLabel}>Nombre de campaña</div>
            <input
              style={inputStyle}
              value={campaignName}
              onChange={e => setCampaignName(e.target.value)}
              required
              maxLength={200}
              placeholder="Ej. Promo verano jazz"
            />
          </div>
          <div>
            <div style={fieldLabel}>Ubicación (slot)</div>
            <select style={inputStyle} value={slot} onChange={e => setSlot(e.target.value)}>
              {AD_SLOT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {slotHint ? (
              <p
                style={{
                  margin: '8px 0 0',
                  fontSize: 10,
                  color: 'rgba(200,168,75,0.85)',
                  lineHeight: 1.45,
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: 'rgba(200,168,75,0.06)',
                  border: '0.5px solid rgba(200,168,75,0.2)',
                }}
              >
                <strong style={{ fontWeight: 700 }}>Tamaño recomendado:</strong> {slotHint}
              </p>
            ) : null}
          </div>
          <div>
            <div style={fieldLabel}>Tipo de medio</div>
            <select style={inputStyle} value={mediaType} onChange={e => setMediaType(e.target.value)}>
              {AD_MEDIA_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={fieldLabel}>Orden en carrusel (0 = primero)</div>
            <input
              style={inputStyle}
              type="number"
              min={0}
              max={99}
              value={sortOrder}
              onChange={e => setSortOrder(Number(e.target.value) || 0)}
            />
          </div>
          <div>
            <div style={fieldLabel}>Estado</div>
            <select style={inputStyle} value={status} onChange={e => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={fieldLabel}>Enlace al hacer clic</div>
            <input
              style={inputStyle}
              type="url"
              value={targetUrl}
              onChange={e => setTargetUrl(e.target.value)}
              placeholder="https://…"
            />
          </div>
          <div>
            <div style={fieldLabel}>Texto alternativo (accesibilidad)</div>
            <input
              style={inputStyle}
              value={altText}
              onChange={e => setAltText(e.target.value)}
              maxLength={500}
              placeholder="Descripción breve del banner"
            />
          </div>
          <div>
            <div style={fieldLabel}>Inicio</div>
            <input
              style={inputStyle}
              type="datetime-local"
              value={startsAt}
              onChange={e => setStartsAt(e.target.value)}
              required
            />
          </div>
          <div>
            <div style={fieldLabel}>Fin</div>
            <input
              style={inputStyle}
              type="datetime-local"
              value={endsAt}
              onChange={e => setEndsAt(e.target.value)}
              required
            />
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={fieldLabel}>Archivo (imagen, GIF o vídeo)</div>
          <input
            type="file"
            accept={
              mediaType === 'video'
                ? 'video/mp4,video/webm'
                : mediaType === 'gif'
                  ? 'image/gif'
                  : 'image/jpeg,image/png,image/webp'
            }
            onChange={e => setFile(e.target.files?.[0] || null)}
            style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}
          />
          {isEdit && mediaUrl && !file ? (
            <p style={{ margin: '6px 0 0', fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
              Deja vacío para mantener el archivo actual.
            </p>
          ) : null}
        </div>

        {previewSrc ? (
          <div
            style={{
              marginTop: 14,
              borderRadius: 10,
              overflow: 'hidden',
              maxHeight: 220,
              background: '#000',
              border: '0.5px solid rgba(255,255,255,0.1)',
            }}
          >
            {previewIsVideo ? (
              <video src={previewSrc} muted playsInline controls style={{ width: '100%', maxHeight: 220, display: 'block' }} />
            ) : (
              <img src={previewSrc} alt="" style={{ width: '100%', maxHeight: 220, objectFit: 'contain', display: 'block' }} />
            )}
          </div>
        ) : null}

        <div style={{ marginTop: 14 }}>
          <div style={fieldLabel}>Notas internas</div>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            style={{ ...inputStyle, resize: 'vertical' }}
            placeholder="Opcional"
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
          <BtnGold type="submit" disabled={saving} style={{ width: 'auto', minWidth: 160 }}>
            {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear campaña'}
          </BtnGold>
          <BtnGhost type="button" onClick={onClose} disabled={saving} style={{ width: 'auto' }}>
            Cancelar
          </BtnGhost>
        </div>
      </form>
    </DCard>
  );
}
